import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Send, MessageSquare, Sparkles } from 'lucide-react';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { usePublicChat } from '@/hooks/usePublicChat';
import { useLanguage } from '@/contexts/LanguageContext';
import { pickByLanguage } from '@/lib/language';
import { cn } from '@/lib/utils';
import { QLIVAR_WELCOME_PT, QLIVAR_WELCOME_EN, QLIVAR_AGENT_CONFIG } from '@/lib/qlivar-agent-instructions';

// ── Hardcoded Q&A fallback (used when VITE_LANDING_AGENT_ID is not set) ────────
interface FallbackEntry {
  keywords: string[];
  pt: string;
  en: string;
}

const FALLBACK: FallbackEntry[] = [
  {
    keywords: ['preço', 'plano', 'custo', 'quanto', 'caro', 'free', 'grátis', 'assinatura', 'price', 'plan', 'cost', 'subscription', 'pagar', 'mensalidade'],
    pt: '💳 **Planos do Qlivar:**\n\n- **Free** — Bio link básico + analytics leve, grátis para sempre\n- **Starter** (R$ 60/mês) — Design premium, captura de leads\n- **Pro** (R$ 120/mês) — Mini loja, domínio próprio, A/B testing, chatbot IA (500 conv/mês)\n- **Business** (R$ 600/mês) — Chatbot IA ilimitado + API + suporte prioritário\n\n💡 Desconto de 20% no anual. Planos pagos têm **7 dias grátis** — cartão obrigatório, cobrança só após o trial!',
    en: '💳 **Qlivar Plans:**\n\n- **Free** — Basic bio link + light analytics, forever free\n- **Starter** ($12/mo) — Premium design, lead capture\n- **Pro** ($24/mo) — Mini store, custom domain, A/B testing, AI chatbot (500 conv/mo)\n- **Business** ($120/mo) — Unlimited AI chatbot + API + priority support\n\n💡 20% off annual billing. Paid plans include a **7-day free trial** — card required, charged only after trial ends!',
  },
  {
    keywords: ['chatbot', 'ia', 'bot', 'agente', 'inteligência', 'ai', 'agent', 'assistente', 'atendimento automático', 'gpt', 'robô'],
    pt: '🤖 **Chatbot IA do Qlivar:**\n\nConfigure em minutos, sem código:\n\n- Treine com informações do seu negócio\n- Responde clientes 24/7 automaticamente\n- Aparece na bio page e na mini loja\n- Captura e qualifica leads com IA\n\n**Pro**: 500 conversas/mês · **Business**: ilimitado\nOs primeiros 7 dias dos planos pagos são grátis (cartão obrigatório)!',
    en: '🤖 **Qlivar AI Chatbot:**\n\nSet up in minutes, no code:\n\n- Train with your business info\n- Replies to customers 24/7 automatically\n- Appears on your bio page and mini store\n- Captures and qualifies leads with AI\n\n**Pro**: 500 conv/mo · **Business**: unlimited\nFirst 7 days of paid plans are free — card required!',
  },
  {
    keywords: ['bio', 'link', 'página', 'linktree', 'linkinbio', 'page', 'profile', 'perfil', 'links', 'criar página'],
    pt: '🔗 **Bio link do Qlivar:**\n\n- Links ilimitados com thumbnail personalizada\n- Domínio próprio (plano Pro+)\n- Temas visuais personalizados\n- Agendamento e expiração automática de links\n- Carregamento em menos de 100ms\n\nComece **grátis** agora!',
    en: '🔗 **Qlivar Bio Link:**\n\n- Unlimited links with custom thumbnail\n- Custom domain (Pro+ plan)\n- Visual themes and customization\n- Link scheduling and auto-expiry\n- Loads in under 100ms\n\nStart **free** now!',
  },
  {
    keywords: ['loja', 'produto', 'venda', 'digital', 'ebook', 'e-book', 'curso', 'infoproduto', 'store', 'sell', 'product', 'vender'],
    pt: '🛍️ **Mini Loja do Qlivar:**\n\n- Venda e-books, cursos, templates e muito mais\n- Pagamento via Pix e cartão de crédito\n- Entrega automática por e-mail\n- Sem taxa de transação adicional\n- Checkout integrado — sem sair da sua página\n\nDisponível nos planos **Pro** e **Business**.',
    en: '🛍️ **Qlivar Mini Store:**\n\n- Sell e-books, courses, templates, digital files\n- Payment via card and local methods\n- Automatic email delivery\n- No extra transaction fee\n- Integrated checkout — no redirect needed\n\nAvailable on **Pro** and **Business** plans.',
  },
  {
    keywords: ['analytics', 'dados', 'estatística', 'clique', 'visita', 'relatório', 'stats', 'data', 'click', 'traffic', 'métricas', 'conversão'],
    pt: '📊 **Analytics do Qlivar:**\n\n- Cliques por link em tempo real\n- Origem do tráfego (Instagram, TikTok etc.)\n- Taxa de conversão por link e campanha\n- Relatórios semanais por e-mail (Pro+)\n\nAnalytics básico no **Free**, avançado a partir do **Pro**.',
    en: '📊 **Qlivar Analytics:**\n\n- Clicks per link in real-time\n- Traffic source (Instagram, TikTok etc.)\n- Conversion rate per link and campaign\n- Weekly email reports (Pro+)\n\nBasic analytics on **Free**, advanced on **Pro** and above.',
  },
  {
    keywords: ['domínio', 'domain', 'url', 'personalizado', 'custom', 'meu site', 'branded', 'meu domínio'],
    pt: '🌐 **Domínio personalizado:**\n\nConecte `seusite.com` ou `links.seusite.com`:\n\n- Configuração simples via DNS (suporte incluso)\n- HTTPS automático e gratuito\n- Disponível no plano **Pro** e **Business**.',
    en: '🌐 **Custom Domain:**\n\nConnect `yoursite.com` or `links.yoursite.com`:\n\n- Simple DNS setup (support included)\n- Automatic free HTTPS\n- Available on **Pro** and **Business** plans.',
  },
  {
    keywords: ['afiliado', 'affiliate', 'indicação', 'referral', 'comissão', 'commission', 'parceria', 'ganhar dinheiro indicando'],
    pt: '🤝 **Programa de Afiliados:**\n\n- **40% de comissão recorrente** por cada indicação\n- Pagamento mensal via Pix\n- Painel com rastreamento em tempo real\n- Sem limite de ganhos\n\nAcesse **qlivar.com/afiliados** para se cadastrar!',
    en: '🤝 **Affiliate Program:**\n\n- **40% recurring commission** per referral\n- Monthly payment\n- Dashboard with real-time tracking\n- No earnings cap\n\nVisit **qlivar.com/affiliates** to sign up!',
  },
  {
    keywords: ['começar', 'criar', 'cadastro', 'conta', 'signup', 'sign up', 'start', 'criar conta', 'register', 'como funciona', 'como usar', 'primeiros passos'],
    pt: '🚀 **Como começar com o Qlivar:**\n\n1. Crie sua conta grátis em **qlivar.com** (sem cartão)\n2. Personalize sua bio page com links e temas visuais\n3. Ative o chatbot de IA e configure em minutos\n4. Compartilhe seu link na bio\n\nMenos de **5 minutos** para ir ao ar!',
    en: '🚀 **How to get started with Qlivar:**\n\n1. Create your free account at **qlivar.com** (no card needed)\n2. Customize your bio page with links and visual themes\n3. Activate the AI chatbot in minutes\n4. Share your bio link\n\nLess than **5 minutes** to go live!',
  },
  {
    keywords: ['diferencial', 'diferença', 'vantagem', 'por que', 'melhor', 'vs', 'comparar', 'why', 'better', 'advantage'],
    pt: '⭐ **Por que escolher o Qlivar?**\n\n- **Tudo em um só lugar** — bio link + mini loja + analytics + chatbot IA\n- **Sem código** — configure tudo no painel em minutos\n- **Starts free** — evolua quando precisar, sem lock-in\n- **Chatbot IA treinado com os seus dados** — não é template genérico\n- **Suporte em português** incluso em todos os planos\n\nComece grátis e teste antes de decidir.',
    en: '⭐ **Why choose Qlivar?**\n\n- **All-in-one** — bio link + mini store + analytics + AI chatbot\n- **No code** — set everything up in minutes from the dashboard\n- **Start free** — upgrade when you need it, no lock-in\n- **AI chatbot trained with your data** — not a generic template\n- **English & Portuguese support** included on all plans\n\nStart free and test before you commit.',
  },
];

function findFallback(query: string, lang: 'pt' | 'en'): string {
  const q = query.toLowerCase();
  for (const entry of FALLBACK) {
    if (entry.keywords.some(k => q.includes(k))) {
      return lang === 'pt' ? entry.pt : entry.en;
    }
  }
  return lang === 'pt'
    ? 'Boa pergunta! 🙂 Para mais detalhes, acesse **qlivar.com** ou crie sua conta grátis e explore a plataforma na prática. Posso ajudar com mais alguma coisa?'
    : 'Great question! 🙂 For more details, visit **qlivar.com** or create your free account and explore hands-on. Can I help with anything else?';
}

// ── Bubble message variants ────────────────────────────────────────────────────
const BUBBLE_MESSAGES = [
  {
    title: { pt: 'Posso te ajudar?', en: 'Can I help you?' },
    body:  { pt: 'Tire dúvidas sobre planos, chatbot e funcionalidades.', en: 'Ask anything about plans, AI chatbot, and features.' },
  },
  {
    title: { pt: 'Seu atendimento no piloto automático', en: 'Your support on autopilot' },
    body:  { pt: 'O chatbot responde seus clientes 24 h — mesmo enquanto você dorme.', en: 'Our chatbot replies to customers 24/7, even while you sleep.' },
  },
  {
    title: { pt: 'Comece grátis, sem cartão', en: 'Start free, no card needed' },
    body:  { pt: '14 dias grátis nos planos pagos. Cancele quando quiser.', en: '14-day free trial on paid plans. Cancel anytime.' },
  },
  {
    title: { pt: 'Curioso como funciona?', en: 'Curious how it works?' },
    body:  { pt: 'Faça uma pergunta — respondo na hora.', en: 'Ask me anything — I\'ll answer right away.' },
  },
] as const;

// ── Props ──────────────────────────────────────────────────────────────────────
export interface LandingChatWidgetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-filled query sent from the hero chat input */
  initialQuery?: string;
}

// ── Component ──────────────────────────────────────────────────────────────────
export function LandingChatWidget({ isOpen, onOpenChange, initialQuery }: LandingChatWidgetProps) {
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });

  const configuredLandingAgentId = (import.meta.env.VITE_LANDING_AGENT_ID as string | undefined)?.trim();
  const agentId = configuredLandingAgentId || '__landing_default__';

  // Always call — hook is a no-op when agentId is null
  const { messages: agentMessages, isLoading: agentLoading, sendMessage: sendToAgent, isLocked: agentLocked, error: agentError, startNewConversation } = usePublicChat(agentId, { language });

  const welcomeContent = t(QLIVAR_WELCOME_PT, QLIVAR_WELCOME_EN);

  const [localMessages, setLocalMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>(() => [
    { role: 'assistant', content: welcomeContent },
  ]);
  const [localLoading, setLocalLoading] = useState(false);
  const [input, setInput] = useState('');
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleDismissed, setBubbleDismissed] = useState(false);
  const [bubbleIdx, setBubbleIdx] = useState(0);
  const [bubbleTyping, setBubbleTyping] = useState(false);
  const bubbleReshowRef = useRef(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sentInitialRef = useRef('');

  const displayMessages = agentId
    ? [{ role: 'assistant' as const, content: welcomeContent }, ...agentMessages]
    : localMessages;
  const isLoading = agentId ? agentLoading : localLoading;
  const isLocked = agentId ? agentLocked : false;

  // Auto-show speech bubble after 5s
  useEffect(() => {
    if (bubbleDismissed || isOpen) return;
    const id = setTimeout(() => setShowBubble(true), 5000);
    return () => clearTimeout(id);
  }, [bubbleDismissed, isOpen]);

  // Hide bubble when chat opens
  useEffect(() => {
    if (isOpen) setShowBubble(false);
  }, [isOpen]);

  // Cycle messages every 8s while bubble is visible
  useEffect(() => {
    if (!showBubble || isOpen) return;
    const id = setInterval(() => {
      setBubbleTyping(true);
      setTimeout(() => {
        setBubbleIdx(i => (i + 1) % BUBBLE_MESSAGES.length);
        setBubbleTyping(false);
      }, 900);
    }, 8000);
    return () => clearInterval(id);
  }, [showBubble, isOpen]);

  // Re-show bubble with a fresh message after 30s (up to 2 times)
  useEffect(() => {
    if (!bubbleDismissed || isOpen || bubbleReshowRef.current >= 2) return;
    const id = setTimeout(() => {
      bubbleReshowRef.current += 1;
      setBubbleIdx(i => (i + 1) % BUBBLE_MESSAGES.length);
      setBubbleDismissed(false);
      setShowBubble(true);
    }, 30_000);
    return () => clearTimeout(id);
  }, [bubbleDismissed, isOpen]);

  // Send initial query when opened from hero section
  useEffect(() => {
    if (isOpen && initialQuery && sentInitialRef.current !== initialQuery) {
      sentInitialRef.current = initialQuery;
      handleSend(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialQuery]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    return () => clearTimeout(id);
  }, [displayMessages, isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      const id = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  // Listen to mobile nav FAB toggle
  useEffect(() => {
    const handler = () => onOpenChange(!isOpen);
    window.addEventListener('toggle-landing-chat', handler);
    return () => window.removeEventListener('toggle-landing-chat', handler);
  }, [isOpen, onOpenChange]);

  const handleSend = async (textOverride?: string) => {
    const q = (textOverride !== undefined ? textOverride : input).trim();
    if (!q || isLoading) return;
    setInput('');

    if (agentId) {
      sendToAgent(q);
      return;
    }

    // Fallback: local simulated response
    setLocalMessages(prev => [...prev, { role: 'user', content: q }]);
    setLocalLoading(true);
    await new Promise<void>(r => setTimeout(r, 650 + Math.random() * 850));
    setLocalMessages(prev => [...prev, { role: 'assistant', content: findFallback(q, language) }]);
    setLocalLoading(false);
  };

  return (
    <>
      {/* ── Speech bubble ── */}
      <AnimatePresence>
        {showBubble && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.86 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.92, transition: { duration: 0.18 } }}
            transition={{ type: 'spring', stiffness: 440, damping: 26 }}
            className="fixed bottom-[80px] sm:bottom-[92px] right-4 sm:right-6 z-50 max-w-[260px] cursor-pointer select-none hidden sm:block"
            onClick={() => onOpenChange(true)}
          >
            <div className="relative bg-[#fffaf5] rounded-2xl rounded-br-sm shadow-[0_8px_28px_rgba(110,79,52,0.12)] border border-[#eadbcc] px-4 py-3 overflow-hidden">
              {/* Gradient accent bar */}
              <div className="absolute top-0 inset-x-0 h-[2.5px] bg-[#f07f45] rounded-t-2xl" />

              {/* Dismiss button */}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); setShowBubble(false); setBubbleDismissed(true); }}
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#fffaf5] shadow-sm border border-[#eadbcc] flex items-center justify-center hover:bg-[#f6ebdf] transition-colors z-10"
                aria-label="Fechar"
              >
                <X className="h-3 w-3 text-[#9a8a7d]" />
              </button>

              <div className="flex items-start gap-2.5 pt-1">
                {/* Animated sparkle */}
                <motion.div
                  animate={{ rotate: [0, 14, -14, 0], scale: [1, 1.2, 1] }}
                  transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 3.5, ease: 'easeInOut' }}
                  className="shrink-0 mt-0.5"
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                </motion.div>

                {/* Cycling message content */}
                <div className="flex-1 min-w-0 min-h-[44px]">
                  <AnimatePresence mode="wait">
                    {bubbleTyping ? (
                      <motion.div
                        key="typing"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="flex items-center gap-1 py-2"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce motion-reduce:animate-none [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce motion-reduce:animate-none [animation-delay:160ms]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce motion-reduce:animate-none [animation-delay:320ms]" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`msg-${bubbleIdx}`}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.22 }}
                      >
                        <p className="text-[13px] font-semibold text-[#2f241e] leading-snug">
                          {t(BUBBLE_MESSAGES[bubbleIdx].title.pt, BUBBLE_MESSAGES[bubbleIdx].title.en)}
                        </p>
                        <p className="text-[11px] text-[#9a8a7d] mt-1 leading-snug">
                          {t(BUBBLE_MESSAGES[bubbleIdx].body.pt, BUBBLE_MESSAGES[bubbleIdx].body.en)}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Tail pointing toward the floating button */}
              <div className="absolute -bottom-[7px] right-7 w-[14px] h-[14px] bg-[#fffaf5] rotate-45 border-r border-b border-[#eadbcc]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Chat panel ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed bottom-[92px] sm:bottom-[100px] right-3 sm:right-6 z-[65] flex flex-col overflow-hidden rounded-2xl shadow-[0_16px_48px_rgba(110,79,52,0.16)] border border-[#eadbcc] bg-[#fffaf5]"
            style={{ width: 'min(380px, calc(100vw - 1.5rem))', maxHeight: 'min(600px, calc(100dvh - 100px))' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3.5 shrink-0 bg-primary">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Bot className="h-4.5 w-4.5 text-white" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <p className="text-[14px] font-bold text-white leading-none">
                    {agentId ? QLIVAR_AGENT_CONFIG.agent_name : 'Qlivar'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8 rounded-full hover:bg-white/20 text-white/70 hover:text-white flex items-center justify-center transition-colors"
                aria-label={t('Fechar', 'Close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
              {displayMessages.map((msg, i) => (
                <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                  {msg.role === 'assistant' && (
                    <div className="h-7 w-7 rounded-full bg-[#fde9d6] flex items-center justify-center shrink-0 mt-0.5">
                      <Bot className="h-3.5 w-3.5 text-[#f07f45]" />
                    </div>
                  )}
                  <div className={cn(
                    'max-w-[85%] rounded-[18px] px-4 py-2.5 text-[14px] leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-[#f07f45] text-white rounded-tr-sm'
                      : 'bg-[#f6ebdf] text-[#2f241e] rounded-tl-sm',
                  )}>
                    {msg.role === 'assistant' ? (
                      <MarkdownRenderer variant="chat">
                        {msg.content}
                      </MarkdownRenderer>
                    ) : (
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isLoading && (
                <div className="flex gap-2 justify-start">
                  <div className="h-7 w-7 rounded-full bg-[#fde9d6] flex items-center justify-center shrink-0">
                    <Bot className="h-3.5 w-3.5 text-[#f07f45]" />
                  </div>
                  <div className="bg-[#f6ebdf] rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
                    <span className="h-2 w-2 rounded-full bg-[#9a8a7d]/50 animate-bounce motion-reduce:animate-none [animation-delay:0ms]" />
                    <span className="h-2 w-2 rounded-full bg-[#9a8a7d]/50 animate-bounce motion-reduce:animate-none [animation-delay:150ms]" />
                    <span className="h-2 w-2 rounded-full bg-[#9a8a7d]/50 animate-bounce motion-reduce:animate-none [animation-delay:300ms]" />
                  </div>
                </div>
              )}

              {/* Agent error bubble */}
              {agentId && agentError && (
                <div className="flex gap-2 justify-start">
                  <div className="h-7 w-7 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="h-3.5 w-3.5 text-rose-500" />
                  </div>
                  <div className="max-w-[85%] rounded-[18px] rounded-tl-sm px-4 py-2.5 text-[14px] bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300">
                    {t('Ops! Algo deu errado. Tente novamente.', 'Oops! Something went wrong. Please try again.')}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-[#eadbcc]/60 shrink-0 pb-[max(12px,env(safe-area-inset-bottom))] bg-[#f8efe6]/30">
              {isLocked ? (
                <div className="flex flex-col items-center gap-2 py-1">
                  <p className="text-[11px] text-[#9a8a7d] text-center">
                    {t('Conversa encerrada.', 'Conversation ended.')}
                  </p>
                  <button
                    type="button"
                    onClick={() => { startNewConversation?.(); }}
                    className="text-[13px] font-semibold text-[#2f241e] hover:text-[#f07f45] transition-colors underline underline-offset-4"
                  >
                    {t('Iniciar nova conversa', 'Start new conversation')}
                  </button>
                </div>
              ) : (
              <form
                onSubmit={e => { e.preventDefault(); handleSend(); }}
                className="flex gap-2"
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder={t('Digite sua pergunta…', 'Type your question…')}
                  disabled={isLoading}
                  className="flex-1 h-11 rounded-full bg-[#f6ebdf]/70 border border-transparent focus:border-[#f07f45]/40 focus:bg-[#fffaf5] px-4 text-[16px] sm:text-[14px] text-[#2f241e] outline-none placeholder:text-[#9a8a7d]/60 transition-colors duration-200 ease-out disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="h-11 w-11 rounded-full bg-[#f07f45] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-95 active:scale-95 transition-colors duration-200 ease-out shrink-0 shadow-[0_4px_14px_rgba(240,127,69,0.28)]"
                  aria-label={t('Enviar', 'Send')}
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Floating button ── */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
        transition={{ delay: 1.4, type: 'spring', stiffness: 280, damping: 22 }}
        className="hidden sm:block fixed sm:bottom-6 sm:right-6 z-[61]"
      >
        <button
          type="button"
          onClick={() => onOpenChange(!isOpen)}
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-[#f07f45] text-white flex items-center justify-center hover:brightness-95 transition-[transform,opacity] duration-200"
          style={{ boxShadow: '0 8px 28px rgba(240,127,69,0.32)' }}
          aria-label={isOpen ? t('Fechar assistente', 'Close assistant') : t('Abrir assistente', 'Open assistant')}
        >
            <AnimatePresence mode="wait">
              {isOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </motion.div>
              ) : (
                <motion.div
                  key="chat"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6" />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
      </motion.div>
    </>
  );
}
